/**
 * useTelehealthMetering — TV-P4-01 / TV-RV-09
 *
 * Records a completed telehealth call against the provider tenant's
 * metered usage via sandman's `/api/v1/billing/telehealth/call-end`.
 *
 * Endpoint contract (sandman TelehealthMeteringController, B2B-M12):
 *   POST /api/v1/billing/telehealth/call-end
 *   Headers: Authorization: Bearer <kc-bearer>
 *   Body:    { callId, durationMinutes, providerTenantId,
 *              providerId?, patientId?, bookingId? }
 *   Returns: { recorded, deduped, eventId, units, sku }
 *
 *   Idempotency: callId is the dedup key. Re-POSTing the same callId
 *   silently no-ops (`deduped: true`), so retries on flaky networks
 *   are safe.
 *
 * Audience-of-record: per OQ-1 Pattern 1, the B2B provider tenant
 * is billed for telehealth minutes regardless of which side ended
 * the call. We pass providerTenantId in the body — sandman trusts
 * the body value over the JWT (a B2C patient can initiate a call
 * to a B2B provider; the JWT tenantId would be wrong otherwise).
 *
 * TV-RV-09 METERING GUARD
 * ────────────────────────
 * A front-desk user joins in visitMode='monitor' — they are an observer,
 * NOT the clinical party. Their join/leave MUST NOT start or stop the
 * clinical billing clock. The mutation early-exits when the caller's
 * visitMode is not 'clinical', logging a debug note so the caller can
 * see the guard fired without a noisy error.
 *
 * Exposed as a TanStack Query mutation. Caller invokes
 * `recordCallEnd.mutateAsync({...})` from LiveVisit's handleEndVisit
 * AFTER the FHIR Encounter is finalized.
 */

import { useMutation } from '@tanstack/react-query';
import { useAuth } from '@/auth/AuthProvider';
import { useSessionStore } from '@/stores/sessionStore';
import { getViewConfig } from '@/auth/viewConfig';

const SANDMAN_URL =
  import.meta.env.VITE_SANDMAN_URL || 'https://sandman.collabkare.io';

export function useTelehealthMetering() {
  const { token, user } = useAuth();
  const tenantId = useSessionStore((s) => s.tenantId);

  // TV-RV-09: resolve visitMode once at hook construction time.
  // getViewConfig never throws; unknown role falls back to 'patient'.
  const visitMode = getViewConfig(user?.role ?? 'patient').visitMode;

  return useMutation({
    mutationKey: ['telehealth-metering'],
    mutationFn: async (args) => {
      // TV-RV-09 GUARD — only the clinical party (provider) bills time.
      // monitor (front-desk) and patient modes are explicitly excluded.
      if (visitMode !== 'clinical') {
        console.debug(
          `[telehealth-metering] skipped — visitMode="${visitMode}" is not clinical; ` +
          'no billing event posted for this join/leave.',
        );
        return { recorded: false, skipped: true, reason: 'non-clinical visitMode' };
      }

      const {
        callId,
        durationSeconds,
        providerId = null,
        patientId = null,
        bookingId = null,
      } = args || {};

      if (!callId || typeof callId !== 'string') {
        throw new Error('useTelehealthMetering: callId is required');
      }
      if (
        durationSeconds == null ||
        !Number.isFinite(durationSeconds) ||
        durationSeconds <= 0
      ) {
        // Don't POST a 0-minute call — the sandman validator rejects
        // (Min(1)), but more importantly recording a zero-duration
        // visit is meaningless. Surface as a soft error so the
        // caller can log and continue.
        throw new Error(
          `useTelehealthMetering: durationSeconds must be > 0 (got ${durationSeconds})`,
        );
      }
      if (!tenantId) {
        throw new Error(
          'useTelehealthMetering: no tenantId in session — cannot determine the audience tenant',
        );
      }
      if (!token) {
        throw new Error(
          'useTelehealthMetering: no Keycloak access token — sandman will reject the POST',
        );
      }

      // Sandman validator caps durationMinutes at 60*12 (12 hours).
      // Anything beyond is almost certainly a bug; clamp + warn.
      let durationMinutes = Math.ceil(durationSeconds / 60);
      const SANDMAN_HARD_CAP = 60 * 12;
      if (durationMinutes > SANDMAN_HARD_CAP) {
        // eslint-disable-next-line no-console
        console.warn(
          `[telehealth-metering] capping ${durationMinutes} min to ${SANDMAN_HARD_CAP} ` +
            '(sandman hard cap; reconsider if a real call exceeded 12h)',
        );
        durationMinutes = SANDMAN_HARD_CAP;
      }

      const body = {
        callId,
        durationMinutes,
        providerTenantId: tenantId,
      };
      if (providerId) body.providerId = providerId;
      if (patientId) body.patientId = patientId;
      if (bookingId) body.bookingId = bookingId;

      const resp = await fetch(
        `${SANDMAN_URL}/api/v1/billing/telehealth/call-end`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(body),
        },
      );

      if (!resp.ok) {
        const text = await resp.text().catch(() => '');
        throw new Error(
          `Sandman call-end POST failed: ${resp.status} ${resp.statusText} ${text}`,
        );
      }
      return resp.json();
    },
  });
}
